import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useMemo } from "react";
import { MemoryMessage } from "../hooks/useMemoryMessages";
import { MemoryCard } from "./MemoryCard";

interface ContactDetailModalProps {
  open: boolean;
  onClose: () => void;
  senderEmail: string;
  messages: MemoryMessage[];
}

export function ContactDetailModal({
  open,
  onClose,
  senderEmail,
  messages,
}: ContactDetailModalProps) {
  const senderMessages = useMemo(() => {
    return messages.filter(
      (m) => m.sender_email?.toLowerCase() === senderEmail.toLowerCase(),
    );
  }, [messages, senderEmail]);

  const contact = senderMessages[0];
  if (!contact) return null;

  const initials = (contact.sender_full_name ?? "A")
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const sourceLabel =
    contact.comment_source === "youtube" ? "Youtube" : "Memorial Page";
  const sourceColor =
    contact.comment_source === "youtube" ? "#ef4444" : "#707070";
  const sourceBg = contact.comment_source === "youtube" ? "#fef2f2" : "#e3e0e0";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          maxHeight: "80vh",
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(0,0,0,0.6)" } },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Paper
          sx={{ borderRadius: 3, p: 2.5, bgcolor: "#f8fafc", boxShadow: 0 }}
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "#94a3b8",
                  fontSize: "16px",
                  fontWeight: 700,
                }}
              >
                {initials}
              </Avatar>
              <Box>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}
                >
                  {contact.sender_full_name ?? "Anonymous"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>
                  {contact.sender_email ?? "No email"}
                </Typography>
              </Box>
            </Stack>

            {/* Action icons */}
            <Stack direction="row" spacing={1}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                <PhoneOutlinedIcon sx={{ fontSize: 24, color: "#94a3b8" }} />
              </Box>
              <Box
                component="a"
                href={
                  contact.sender_email
                    ? `mailto:${contact.sender_email}`
                    : undefined
                }
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 24, color: "#94a3b8" }} />
              </Box>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 24, color: "#94a3b8" }} />
              </Box>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Chip
              label={sourceLabel}
              size="small"
              sx={{
                bgcolor: sourceBg,
                color: sourceColor,
                fontWeight: 600,
                fontSize: "11px",
                height: 20,
                borderRadius: "4px",
              }}
            />
            <Chip
              label={`${senderMessages.length} ${senderMessages.length === 1 ? "Memory" : "Memories"}`}
              size="small"
              sx={{
                bgcolor: "#f1f5f9",
                color: "#64748b",
                fontWeight: 600,
                fontSize: "11px",
                height: 20,
                borderRadius: "4px",
              }}
            />
          </Stack>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              color: "#1e293b",
              mb: 1.5,
            }}
          >
            Memories shared
          </Typography>

          <Box
            sx={{
              maxHeight: 360,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#e2e8f0",
                borderRadius: 4,
              },
            }}
          >
            {senderMessages.map((msg, index) => (
              <MemoryCard
                key={msg.id}
                msg={msg}
                isLast={index === senderMessages.length - 1}
              />
            ))}
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}
